import httpservice from "./httpservice";
import fileDownloadService from "./fileDownloadService";

class WalletService {
  constructor() {
    this.endpoints = {
      wallet: "/admin/cp/wallet",
      payouts: "/admin/cp/wallet/payouts",
      payoutsExport: "/admin/cp/wallet/payouts/export",
      banks: "/admin/cp/wallet/banks",
      pin: "/admin/cp/wallet/pin",
      pinResetRequest: "/admin/cp/wallet/pin/reset/request",
      pinReset: "/admin/cp/wallet/pin/reset",
    };
  }

  async getWallet(userId) {
    if (!userId) return null;
    try {
      const res = await httpservice.get(`/admin/cp/wallet/${userId}`);
      if (res?.data) return res;
    } catch (e) {
      // quiet fallback to params route
    }
    try {
      const res = await httpservice.get(this.endpoints.wallet, { params: { userId } });
      if (res?.data) return res;
    } catch (e) {
      // quiet fallback
    }
    return null;
  }

  async getPayouts(userId, query = {}) {
    if (!userId) return null;
    const params = {
      userId,
      page: query.page || 1,
      limit: query.limit || 20,
    };
    if (query.status && query.status.toUpperCase() !== "ALL") {
      params.status = query.status.toLowerCase();
    }
    if (query.dateFrom) params.dateFrom = query.dateFrom;
    if (query.dateTo) params.dateTo = query.dateTo;

    try {
      const res = await httpservice.get(this.endpoints.payouts, { params });
      const payloadData = res?.data?.data || res?.data || {};
      const docs = payloadData.data || payloadData.docs || (Array.isArray(payloadData) ? payloadData : []);

      const getBankNameByCode = (code) => {
        const codes = {
          "058": "GTBank",
          "011": "First Bank",
          "044": "Access Bank",
          "033": "UBA",
          "057": "Zenith Bank",
          "035": "Wema Bank",
          "215": "Unity Bank",
          "050": "Ecobank",
          "070": "Fidelity Bank",
          "082": "Keystone Bank",
          "221": "Stanbic IBTC",
          "232": "Sterling Bank",
          "032": "Union Bank",
          "101": "Providus Bank",
          "301": "Jaiz Bank",
          "068": "Standard Chartered",
          "999992": "Opay",
          "999991": "Palmpay",
          "50515": "Moniepoint"
        };
        return codes[code] || "";
      };

      const mappedDocs = docs.map(doc => ({
        ...doc,
        accountPaidTo: doc.accountPaidTo ? {
          ...doc.accountPaidTo,
          bankName: doc.accountPaidTo.bankName || doc.accountPaidTo.accountName || getBankNameByCode(doc.accountPaidTo.bankCode) || "Bank"
        } : null
      }));

      return {
        data: {
          ...payloadData,
          docs: mappedDocs,
          data: mappedDocs
        }
      };
    } catch (e) {
      // quiet fail
    }
    return { data: { docs: [], data: [], totalDocs: 0, totalPages: 1 } };
  }

  async exportPayouts(userId, query = {}, callback) {
    const params = {};
    if (userId) params.userId = userId;
    if (query.status && query.status.toUpperCase() !== "ALL") params.status = query.status.toLowerCase();
    if (query.dateFrom) params.dateFrom = query.dateFrom;
    if (query.dateTo) params.dateTo = query.dateTo;

    const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const fileName = `user-payouts-${today}.csv`;
    return fileDownloadService.download(
      this.endpoints.payoutsExport || "/admin/cp/wallet/payouts/export",
      fileName,
      callback,
      params
    );
  }

  async getBanks(userId) {
    return await httpservice.get(this.endpoints.banks, { params: { userId } });
  }

  async payout(data) {
    return await httpservice.post(this.endpoints.payouts, data);
  }

  async getPinStatus() {
    return await httpservice.get(this.endpoints.pin);
  }

  async setPin(data) {
    return await httpservice.post(this.endpoints.pin, data);
  }

  async changePin(data) {
    return await httpservice.put(this.endpoints.pin, data);
  }

  async requestPinReset() {
    return await httpservice.post(this.endpoints.pinResetRequest);
  }

  async resetPin(data) {
    return await httpservice.post(this.endpoints.pinReset, data);
  }

  // --- Paystack Balance & Fundings (Section 5) ---
  async getPaystackBalance() {
    return await httpservice.get("/admin/cp/wallet/paystack/balance");
  }

  async logFunding(data) {
    return await httpservice.post("/admin/cp/wallet/paystack/fundings", data);
  }

  async getFundings(query = {}) {
    const params = {};
    if (query?.page) params.page = query.page;
    if (query?.limit) params.limit = query.limit;
    return await httpservice.get("/admin/cp/wallet/paystack/fundings", { params });
  }
}

export default new WalletService();
